import * as React from 'react'

import { observer } from 'mobx-react'

import { IFormMaterialResource, LocationType } from '~/client/graph'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useMaterialResourceStore } from '~/client/src/shared/hooks/useMaterialResourceStore'

import SitePermitCreationFormStore from '../../../../../WorkflowCreationForm.store'
import FormResourcePicker from './FormResourcePicker'

const getMaterialLocationType = () => LocationType.Material

interface IProps {
  store: SitePermitCreationFormStore
  projectId?: string
  startCreatingRef?: React.MutableRefObject<(() => void) | null>
  onClose?: () => void
}

const FormMaterialResourcePicker: React.FC<IProps> = observer(
  ({ store, projectId, startCreatingRef, onClose }) => {
    const { records, loading, schema } = useMaterialResourceStore(projectId)

    // Material resource values key on locationId (not id), so they drive
    // pre-selection / keep-selected and the single-mode "already used in another
    // slot" disabling. Read observables inline for MobX tracking, then memoize.
    const values = store.selectedFieldValues as IFormMaterialResource[]
    const editingIndex = store.selectedFieldIndex ?? -1
    const isBulkMode = store.selectedField?.isMultiple
    const isAdminOrFormsMaster = store.isAdminOrFormsMaster

    const { allSelectedIds, otherSlotIds, currentSlotIds } =
      React.useMemo(() => {
        const all: string[] = []
        const others = new Set<string>()
        const current: string[] = []
        if (values?.length) {
          values.forEach((v, i) => {
            if (v?.locationId) {
              const id = String(v.locationId)
              all.push(id)
              if (i !== editingIndex) others.add(id)
              else current.push(id)
            }
          })
        }
        return {
          allSelectedIds: all,
          otherSlotIds: others,
          currentSlotIds: current,
        }
      }, [values, editingIndex])

    // Read the readiness flag + map reference so this observer recomputes the
    // callbacks when controls / permittedCompanies change live while the modal
    // is open (FormResourcePicker is not an observer). Mirrors FormEquipmentPicker.
    const isMaterialControlsReady = store.areResourceControlsReadyForType(
      ResourceTypeId.MATERIALS,
    )
    const freshResourcePermittedCompanies =
      store.freshResourcePermittedCompanies

    // Materials surface only the controls they have: not-bookable,
    // reserved-for and max booking duration. Closures and overlap are
    // equipment-only, so no overlap query runs.
    const getNote = React.useCallback(
      (recordId: string) => store.getResourceNote(recordId),
      // freshResourcePermittedCompanies drives recompute on live restriction edits
      [store, freshResourcePermittedCompanies],
    )

    const isItemDisabled = React.useCallback(
      (recordId: string) => {
        // A material already chosen in another single-select slot is blocked for
        // everyone, independent of restrictions or role.
        if (!isBulkMode && otherSlotIds.has(recordId)) return true
        // Admins/forms-masters bypass restriction-based disabling (time/company),
        // matching equipment; the hide rule (recordPredicate) still applies.
        if (isAdminOrFormsMaster) return false
        // Pessimistic until the material controls have settled, so a restricted
        // row is never briefly shown enabled before the sync commits.
        if (!isMaterialControlsReady) return true
        return getNote(recordId)?.tone === 'blocked'
      },
      [
        isBulkMode,
        otherSlotIds,
        isAdminOrFormsMaster,
        isMaterialControlsReady,
        getNote,
      ],
    )

    return (
      <FormResourcePicker
        store={store}
        resourceTypeId={ResourceTypeId.MATERIALS}
        getLocationType={getMaterialLocationType}
        records={records}
        loading={loading}
        projectId={projectId}
        defaultName="New Material"
        startCreatingRef={startCreatingRef}
        onClose={onClose}
        // Bulk shows every chosen material selected; single-select shows only the
        // edited slot's value, so materials in other slots stay un-selected and the
        // "already used in another slot" disable rule still applies to them.
        initialSelectedIds={isBulkMode ? allSelectedIds : currentSlotIds}
        isItemDisabled={isItemDisabled}
        getNote={getNote}
        recordPredicate={store.resourceVisibilityPredicate}
        resourceSchema={schema}
      />
    )
  },
)

export default FormMaterialResourcePicker
